import { LinearProgress, LinearProgressProps, keyframes } from '@mui/material'

import { MESSAGES } from '@/constants/messages'
import { numericUtils } from '@/utils/numeric'

const STRIPE_WIDTH = 4
const STRIPE_TILE = STRIPE_WIDTH * 2 * Math.SQRT2

const stripes = keyframes`
  to { background-position: ${STRIPE_TILE}px 0; }
`

interface BatteryLevelBarProps extends Omit<LinearProgressProps, 'variant' | 'value'> {
  soc?: number | null
  height?: number
}

const BatteryLevelBar: React.FC<BatteryLevelBarProps> = ({ soc, height = 10, sx, ...props }) => {
  const hasSoc = numericUtils.isValidNumericInput(soc)
  const level = hasSoc ? Math.min(Math.max(soc, 0), 100) : 0

  return (
    <LinearProgress
      variant="determinate"
      value={level}
      aria-label={MESSAGES.CHARGING_SESSION.BATTERY_LEVEL_ARIA_LABEL}
      aria-valuenow={hasSoc ? level : undefined}
      aria-valuetext={hasSoc ? undefined : MESSAGES.CHARGING_SESSION.BATTERY_UNKNOWN}
      sx={{
        height,
        borderRadius: height,
        ...(!hasSoc && {
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            opacity: 0.25,
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              currentColor 0 ${STRIPE_WIDTH}px,
              transparent ${STRIPE_WIDTH}px ${STRIPE_WIDTH * 2}px
            )`,
            backgroundSize: `${STRIPE_TILE}px ${STRIPE_TILE}px`,
            animation: `${stripes} 1s linear infinite`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          },
        }),
        ...sx,
      }}
      {...props}
    />
  )
}

export default BatteryLevelBar
